import React, { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow, format } from "date-fns";
import { trpc } from "../../lib/api/trpc";

type AuditLogEntry = {
  id: string;
  organizationId: string;
  userId: string | null;
  userEmail: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  resourceName: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

type AuditLogResponse = {
  logs: AuditLogEntry[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

const actionLabels: Record<string, string> = {
  "document.upload": "Uploaded document",
  "document.delete": "Deleted document",
  "document.version": "Uploaded new version",
  "user.create": "Created user",
  "user.update_role": "Changed user role",
  "user.archive": "Archived user",
  "collection.create": "Created collection",
  "collection.delete": "Deleted collection",
};

function actionBadgeClass(action: string): string {
  const base =
    "inline-flex items-center text-[11.5px] font-medium px-[9px] py-[3px] rounded-[7px]";
  if (
    action.startsWith("document.upload") ||
    action.startsWith("user.create") ||
    action.startsWith("collection.create")
  ) {
    return `${base} bg-[var(--ok-bg)] text-[var(--ok-text)]`;
  }
  if (
    action.startsWith("document.delete") ||
    action.startsWith("user.archive") ||
    action.startsWith("collection.delete")
  ) {
    return `${base} bg-[var(--err-bg)] text-[var(--err-text)]`;
  }
  if (
    action.startsWith("document.version") ||
    action.startsWith("document.reprocess") ||
    action.startsWith("user.update_role")
  ) {
    return `${base} bg-[var(--warn-bg)] text-[var(--warn-text)]`;
  }
  return `${base} bg-[var(--surface-2)] text-[var(--text-2)]`;
}

function formatMetadata(
  action: string,
  metadata: Record<string, unknown> | null,
): string | null {
  if (!metadata) return null;
  if (action === "user.update_role" && metadata.oldRole && metadata.newRole) {
    return `Role changed from ${metadata.oldRole} to ${metadata.newRole}`;
  }
  if (action === "document.version" && metadata.previousVersionId) {
    return `Previous version: ${metadata.previousVersionId}`;
  }
  return null;
}

function exportToCsv(logs: AuditLogEntry[]) {
  const headers = ["When", "Who", "Action", "Resource", "Details"];
  const rows = logs.map((log) => [
    format(new Date(log.createdAt), "yyyy-MM-dd HH:mm:ss"),
    log.userEmail || "System",
    actionLabels[log.action] || log.action,
    log.resourceName || "-",
    formatMetadata(log.action, log.metadata) || "",
  ]);

  const csv = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-log-${format(new Date(), "yyyy-MM-dd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const page = parseInt(searchParams.get("page") || "0", 10);
  const resourceType = searchParams.get("resourceType") || undefined;
  const userFilter = searchParams.get("user") || undefined;
  const dateFrom = searchParams.get("dateFrom") || undefined;
  const dateTo = searchParams.get("dateTo") || undefined;

  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data, isLoading, isError } = trpc.analytics.getAuditLog.useQuery({
    page,
    pageSize: 50,
    resourceType,
    userId: userFilter,
    dateFrom,
    dateTo,
  });

  const result = data as AuditLogResponse | undefined;

  // Fetch org members for the user filter dropdown
  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data: membersData } = trpc.organization.listMembers.useQuery({
    page: 0,
    limit: 100,
  });
  type MemberItem = {
    user: { id: string; email: string; firstName: string; lastName: string };
  };
  const members = ((membersData as { members?: MemberItem[] })?.members ||
    (membersData as MemberItem[]) ||
    []) as MemberItem[];

  const updateFilter = useCallback(
    (key: string, value: string) => {
      const params = Object.fromEntries(searchParams.entries());
      if (!value) {
        delete params[key];
      } else {
        params[key] = value;
      }
      params.page = "0";
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (p: number) => {
      const params = Object.fromEntries(searchParams.entries());
      params.page = String(p);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-8 animate-pulse">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 rounded-[9px] bg-[var(--surface-2)]" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-[var(--err-bg)] border border-[var(--err-bg)] text-[var(--err-text)] rounded-[12px] p-4">
        <p className="text-[13px]">Failed to load audit log.</p>
      </div>
    );
  }

  return (
    <div className="space-y-[18px]">
      {/* Filter bar */}
      <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={resourceType || ""}
            onChange={(e) => updateFilter("resourceType", e.target.value)}
            className="h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)]"
          >
            <option value="">All actions</option>
            <option value="document">Documents</option>
            <option value="user">Users</option>
            <option value="collection">Collections</option>
          </select>
          <select
            value={userFilter || ""}
            onChange={(e) => updateFilter("user", e.target.value)}
            className="flex-1 min-w-[180px] h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)]"
          >
            <option value="">All users</option>
            {members.map((m) => (
              <option key={m.user.id} value={m.user.email}>
                {m.user.firstName} {m.user.lastName} ({m.user.email})
              </option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom || ""}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
            className="h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)]"
          />
          <input
            type="date"
            value={dateTo || ""}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
            className="h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)]"
          />
          {result && result.logs.length > 0 && (
            <button
              onClick={() => exportToCsv(result.logs)}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-[9px] bg-[var(--accent)] text-[#F4F2E8] text-[13px] font-semibold hover:bg-[var(--accent-strong)] transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      {result && result.logs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-[13px] text-[var(--text-3)]">
            No audit events recorded yet.
          </p>
        </div>
      ) : result ? (
        <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-[var(--surface-2)] border-b border-[var(--border-2)]">
              <tr>
                <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px] w-8" />
                <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                  When
                </th>
                <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                  Who
                </th>
                <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                  Action
                </th>
                <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                  Resource
                </th>
              </tr>
            </thead>
            <tbody>
              {result.logs.map((log) => {
                const isExpanded = expandedRows.has(log.id);
                const details = formatMetadata(log.action, log.metadata);
                return (
                  <React.Fragment key={log.id}>
                    <tr
                      onClick={() => details && toggleRow(log.id)}
                      className={`${details ? "cursor-pointer" : ""} border-b border-[var(--border-2)] hover:bg-[var(--surface-2)] transition-colors`}
                    >
                      <td className="px-5 py-3 w-8">
                        {details &&
                          (isExpanded ? (
                            <ChevronUpIcon className="h-4 w-4 text-[var(--text-faint)]" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 text-[var(--text-faint)]" />
                          ))}
                      </td>
                      <td
                        className="px-5 py-3 whitespace-nowrap text-[13px] text-[var(--text-3)]"
                        title={format(
                          new Date(log.createdAt),
                          "MMM d, yyyy h:mm:ss a",
                        )}
                      >
                        {formatDistanceToNow(new Date(log.createdAt), {
                          addSuffix: true,
                        })}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-[13px] font-mono text-[var(--text-2)]">
                        {log.userEmail || "System"}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-[13px]">
                        <span className={actionBadgeClass(log.action)}>
                          {actionLabels[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-[13px] text-[var(--text)]">
                        {log.resourceName || "-"}
                      </td>
                    </tr>
                    {isExpanded && details && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-12 py-3 bg-[var(--surface-2)] text-[13px] text-[var(--text-3)]"
                        >
                          {details}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {result.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border-2)]">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page <= 0}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Previous
              </button>
              <span className="text-[13px] text-[var(--text-3)]">
                Page {page + 1} of {result.pagination.totalPages} (
                {result.pagination.total} events)
              </span>
              <button
                onClick={() =>
                  setPage(Math.min(result.pagination.totalPages - 1, page + 1))
                }
                disabled={page >= result.pagination.totalPages - 1}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
